import { px } from '@adsk/alloy-react-helpers';

import { AlloyLegacyThemeDesignTokens, BadgeDesignTokens } from '../types';
import { WeaveTheme } from '../types/weaveTypes';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getBadgeTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): BadgeDesignTokens => ({
  'border-radius': px(theme.borderRadius.pill),
  'font-size': theme.typography.labelMedium.fontSize,
  'font-weight': theme.typography.labelMedium.fontWeight,
  'line-height': theme.typography.labelMedium.lineHeight,
  small: {
    'font-size': theme.typography.labelSmall.fontSize,
    'font-weight': theme.typography.labelSmall.fontWeight,
    'line-height': theme.typography.labelSmall.lineHeight,
  },
  text: {
    'padding-top': px(theme.spacing.XXXS),
    'padding-bottom': px(theme.spacing.XXXS),
    'padding-left': px(theme.spacing.XS),
    'padding-right': px(theme.spacing.XS),
    height: theme.typography.labelMedium.lineHeight,
  },
  'text-color': theme.tokens.colors.text.inverse.value,
  'secondary-text-color': theme.tokens.colors.text.secondary.value,
  substatus: {
    'font-family': theme.typography.bodyMedium.fontFamily,
    'font-size': theme.typography.bodyMedium.fontSize,
    'font-weight': theme.typography.bodyMedium.fontWeight,
    'line-height': theme.typography.bodyMedium.lineHeight,
    small: {
      'font-family': theme.typography.bodySmall.fontFamily,
      'font-size': theme.typography.bodySmall.fontSize,
      'font-weight': theme.typography.bodySmall.fontWeight,
      'line-height': theme.typography.bodySmall.lineHeight,
    },
  },
  disabled: {
    'text-color': theme.tokens.colors.text.inactive.value,
    'background-color': theme.tokens.colors.background.neutral.inactive.value,
    opacity: 1,
  },
  outlined: {
    'border-width': '1px',
    'border-style': 'solid',
    'border-color': theme.tokens.colors.border.value,
  },
  dot: {
    l: {
      height: '16px',
      width: '16px',
      inner: {
        height: '6px',
        width: '6px',
        'background-color': theme.tokens.colors.background.primary.value,
      },
    },
    s: { height: '8px', width: '8px' },
  },
  l: {
    'icon-container': { height: '24px', width: '24px' },
    icon: { width: '16px' },
  },
  s: {
    'icon-container': { height: '16px', width: '16px' },
    icon: { width: '12px' },
  },
  success: {
    'background-color': theme.tokens.colors.background.success.value,
    'text-color': theme.tokens.colors.text.inverse.value,
    icon: { fill: theme.tokens.colors.text.inverse.value },
  },
  error: {
    'background-color': theme.tokens.colors.background.error.value,
    'text-color': theme.tokens.colors.text.inverse.value,
    icon: { fill: theme.tokens.colors.text.inverse.value },
  },
  warning: {
    'background-color': theme.tokens.colors.background.warning.value,
    'text-color': theme.tokens.colors.text.inverse.value,
    icon: { fill: theme.tokens.colors.text.inverse.value },
  },
  new: {
    'background-color': theme.tokens.colors.background.information.value,
    'text-color': theme.tokens.colors.text.inverse.value,
  },
  info: {
    'background-color':
      theme.tokens.colors.background.information.subtler.value,
    'text-color': theme.tokens.colors.text.primary.value,
  },
});

export const getBadgeTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): BadgeDesignTokens => {
  const badge = tokens?.component?.badge;

  return {
    'border-radius': badge?.['border-radius'],
    'font-size': badge?.['font-size'],
    'font-weight': badge?.['font-weight'],
    'line-height': badge?.['line-height'],
    small: {
      'font-size': badge?.['font-size'],
      'font-weight': badge?.['font-weight'],
      'line-height': badge?.['line-height'],
    },
    text: {
      'padding-top': badge?.text?.['padding-top'],
      'padding-bottom': badge?.text?.['padding-bottom'],
      'padding-left': badge?.text?.['padding-left'],
      'padding-right': badge?.text?.['padding-right'],
      height: badge?.text?.height,
    },
    'text-color': tokens?.semantic?.['text-color']?.['on-status']?.dark,
    'primary-text-color':
      tokens?.semantic?.['text-color']?.['on-status']?.light,
    'secondary-text-color': tokens?.generic?.color?.charcoal?.[700]?.[100],
    substatus: {
      'font-family': fontFamily,
      'font-size': badge?.['font-size'],
      'font-weight': badge?.['font-weight'],
      'line-height': badge?.['line-height'],
      small: {
        'font-family': fontFamily,
        'font-size': badge?.['font-size'],
        'font-weight': badge?.['font-weight'],
        'line-height': badge?.['line-height'],
      },
    },
    disabled: {
      opacity: tokens?.semantic?.opacity?.disabled,
    },
    outlined: {
      'border-width': badge?.outlined?.['border-width'],
      'border-style': badge?.outlined?.['border-style'],
      'border-color': tokens?.semantic?.['border-color']?.medium?.default,
    },
    dot: {
      l: {
        height: badge?.dot?.l?.height,
        width: badge?.dot?.l?.width,
      },
      s: {
        height: badge?.dot?.s?.height,
        width: badge?.dot?.s?.width,
      },
    },
    l: {
      'icon-container': {
        height: badge?.l?.['icon-container']?.height,
        width: badge?.l?.['icon-container']?.width,
      },
      icon: { width: badge?.l?.icon?.width },
    },
    s: {
      'icon-container': {
        height: badge?.s?.['icon-container']?.height,
        width: badge?.s?.['icon-container']?.width,
      },
      icon: { width: badge?.s?.icon?.width },
    },
    success: badge?.success,
    error: badge?.error,
    warning: badge?.warning,
    new: badge?.new,
    info: {
      'background-color': tokens?.generic?.color?.blue?.[100]?.[100],
      'text-color': tokens?.semantic?.['text-color']?.['on-status']?.light,
    },
  };
};
